import { z } from "zod";

/**
 * Chat key-figures card (EDG-592; perplexica `dynamic-ui/layout.ts`):
 * Card{title?} > Grid{columns?} > Metric children, plus an optional Text note.
 * The server cleans every node before it reaches the client, so these schemas
 * document the wire shape; renderers still tolerate missing or odd props.
 */

export const LAYOUT_TONES = [
  "default",
  "muted",
  "positive",
  "negative",
  "warning",
  "brand",
] as const;

export type LayoutTone = (typeof LAYOUT_TONES)[number];

const toneZ = z
  .enum(LAYOUT_TONES)
  .optional()
  .describe(
    "Semantic color: positive/negative mean good/bad for the user, muted is secondary text.",
  );

export const layoutCardPropsZ = z.object({
  title: z.string().optional().describe("Card heading."),
});

export const layoutGridPropsZ = z.object({
  columns: z
    .union([z.literal(2), z.literal(3)])
    .optional()
    .describe("Equal columns; narrow screens show at most 2."),
});

export const layoutTextPropsZ = z.object({
  text: z.string().describe("Plain text; only inline `**bold**` is supported."),
  size: z.enum(["sm", "md"]).optional(),
  tone: toneZ,
  weight: z.enum(["regular", "semibold"]).optional(),
});

export const layoutMetricPropsZ = z.object({
  label: z.string(),
  value: z.string(),
  caption: z.string().optional(),
  tone: toneZ.describe("Colors the value."),
});

export type LayoutCardProps = z.infer<typeof layoutCardPropsZ>;
export type LayoutGridProps = z.infer<typeof layoutGridPropsZ>;
export type LayoutTextProps = z.infer<typeof layoutTextPropsZ>;
export type LayoutMetricProps = z.infer<typeof layoutMetricPropsZ>;

export const LAYOUT_CATALOG_COMPONENTS = {
  Card: {
    props: layoutCardPropsZ,
    description: "Bordered card; children stack vertically. Root only.",
  },
  Grid: {
    props: layoutGridPropsZ,
    description: "Equal-width grid of Metric.",
  },
  Text: {
    props: layoutTextPropsZ,
    description: "Note text with inline **bold**.",
  },
  Metric: {
    props: layoutMetricPropsZ,
    description: "Label, large value and optional caption.",
  },
} as const;
